import { useEffect, useState } from "preact/hooks"
import { Loader2, Paperclip, X } from "lucide-react"

import type { ShortPayDisputePreview, ShortPayJson } from "@/api/accounting.tsx"
import {
  fetchDeductionShortPayDisputePreview,
  fetchShortPayDisputePreview,
  fileDeductionShortPayDispute,
  fileShortPayDispute,
} from "@/api/accounting.tsx"
import type { Deduction } from "@/api/deduction.tsx"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { exactCurrencyFormatter } from "@/utils"

/**
 * What the dialog is chasing: a recorded short pay (from the accounting-backed short pays screen),
 * or the invoice payment row itself (from the deduction detail page, no accounting needed).
 */
export type ShortPayDisputeTarget =
  | { kind: "short_pay"; short_pay: ShortPayJson }
  | { kind: "deduction"; deduction: Pick<Deduction, "id" | "invoice_number"> }

export interface ShortPayDisputeDialogProps {
  /** The short pay being chased; `null` closes the dialog. */
  target: ShortPayDisputeTarget | null
  onClose: () => void
  /** Called after a successful file so the list can refresh. */
  onFiled: () => void
}

/** One read-only line of the composed email, so the user sees exactly what goes out. */
function EmailField({ label, value }: { label: string; value: string }) {
  return (
    <div class="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
      <span class="w-20 shrink-0 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
      <span class="break-all text-sm text-slate-900">{value}</span>
    </div>
  )
}

/** Stable identity for the target, so effects re-run only when the thing being chased changes. */
export function short_pay_target_key(target: ShortPayDisputeTarget | null): string | null {
  if (!target) return null
  return target.kind === "short_pay" ? `short_pay:${target.short_pay.id}` : `deduction:${target.deduction.id}`
}

export function short_pay_target_invoice_number(target: ShortPayDisputeTarget): string {
  return target.kind === "short_pay" ? target.short_pay.invoice_number : target.deduction.invoice_number
}

/** The balance to file: the typed one when the server could not derive it, else the server's. */
export function effective_short_pay_amount(preview: Pick<ShortPayDisputePreview, "amount"> | null, typed: string): number {
  const typed_amount = Number.parseFloat(typed)
  if (Number.isFinite(typed_amount) && typed_amount > 0) return Math.round(typed_amount * 100) / 100
  const server_amount = Number.parseFloat(preview?.amount ?? "0")
  return Number.isFinite(server_amount) && server_amount > 0 ? server_amount : 0
}

/**
 * Review-and-send for a short pay dispute.
 *
 * The email is composed server-side from the format the distributor has actually paid out on, so
 * the dialog's job is to show it, collect the one thing we cannot produce ourselves — the invoice
 * PDF, which lives in the accounting system — and send. Reached from an invoice row without an
 * accounting link it also has to collect the amount, because the remittance says what was paid,
 * not what was owed.
 */
export function ShortPayDisputeDialog({ target, onClose, onFiled }: ShortPayDisputeDialogProps) {
  const [preview, setPreview] = useState<ShortPayDisputePreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [amount_input, setAmountInput] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const target_key = short_pay_target_key(target)
  // The amount the draft is composed for. Re-fetching on every keystroke would flicker the draft,
  // so the preview follows the amount only once the field is left.
  const [composed_amount, setComposedAmount] = useState<number | null>(null)

  useEffect(() => {
    if (!target) {
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    const request =
      target.kind === "short_pay"
        ? fetchShortPayDisputePreview(target.short_pay.id)
        : fetchDeductionShortPayDisputePreview(target.deduction.id, composed_amount)
    void request.then((res: Awaited<ReturnType<typeof fetchShortPayDisputePreview>>) => {
      if (cancelled) {
        return
      }
      setLoading(false)
      if (!res.ok) {
        setError(res.error.message)
        return
      }
      setPreview(res.value.data)
      setMessage(res.value.data.body)
    })
    return () => {
      cancelled = true
    }
  }, [target_key, composed_amount])

  // Fresh target: forget the previous one's attachments and typed amount.
  useEffect(() => {
    setFiles([])
    setAmountInput("")
    setComposedAmount(null)
    setPreview(null)
  }, [target_key])

  function handle_files(e: Event) {
    const input = e.target as HTMLInputElement
    if (!input.files) {
      return
    }
    setFiles((current: File[]) => [...current, ...Array.from(input.files!)])
    // Clear the input so re-picking the same file still fires a change event.
    input.value = ""
  }

  function remove_file(index: number) {
    setFiles((current: File[]) => current.filter((_: File, i: number) => i !== index))
  }

  function handle_amount_blur() {
    const amount = effective_short_pay_amount(null, amount_input)
    setComposedAmount(amount > 0 ? amount : null)
  }

  async function handle_send() {
    if (!target || !preview) {
      return
    }
    setSending(true)
    setError(null)
    const existing = preview.attachments.map((a: { s3_uri: string }) => a.s3_uri)
    const res =
      target.kind === "short_pay"
        ? await fileShortPayDispute(target.short_pay.id, message, files, existing)
        : await fileDeductionShortPayDispute(
            target.deduction.id,
            effective_short_pay_amount(preview, amount_input),
            message,
            files,
            existing
          )
    setSending(false)
    if (!res.ok) {
      setError(res.error.message)
      return
    }
    onFiled()
    onClose()
  }

  const blocked = preview?.blocked_reason
  // The invoice is the one attachment we cannot produce, and the distributor will not act without
  // it — so sending with nothing attached is almost certainly a mistake.
  const no_invoice_attached = files.length === 0
  // Only the invoice-row path asks for an amount; the short pay row already records it.
  const asks_for_amount = target?.kind === "deduction"
  const amount = effective_short_pay_amount(preview, amount_input)
  const amount_missing = asks_for_amount && amount <= 0
  const amount_hint =
    preview?.amount_source === "accounting_invoice"
      ? "From the accounting invoice total less what the check settled."
      : preview?.amount_source === "short_pay"
        ? "From the short pay recorded when the check was applied."
        : "Enter the amount the invoice was short paid — the remittance only shows what was paid."

  return (
    <Dialog open={!!target} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent class="max-w-2xl">
        <DialogHeader>
          <DialogTitle>File short pay dispute</DialogTitle>
          <DialogDescription>
            {target && (
              <>
                Asking for {amount > 0 ? `the ${exactCurrencyFormatter(amount)}` : "the balance"} left unpaid on invoice{" "}
                <span class="font-medium text-slate-700">{short_pay_target_invoice_number(target)}</span>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && !preview && (
          <div class="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Composing the request…
          </div>
        )}

        {preview && (
          <div class="space-y-4">
            {/* No subject means we have no channel for this distributor — show the reason alone
                rather than a draft addressed to the wrong company. */}
            {blocked && (
              <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{blocked}</div>
            )}
            {preview.already_filed && (
              <div class="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                A dispute for this short pay was already filed
                {preview.already_filed.sent_at ? " and sent" : ", and is queued to send"}.
              </div>
            )}

            {preview.subject && (
              <>
                {asks_for_amount && (
                  <div class="space-y-1.5">
                    <Label htmlFor="short-pay-dispute-amount">Amount short paid</Label>
                    <div class="flex items-center gap-3">
                      <Input
                        id="short-pay-dispute-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        class="w-40"
                        placeholder={preview.amount !== "0" ? preview.amount : "0.00"}
                        value={amount_input}
                        onInput={(e: Event) => setAmountInput((e.target as HTMLInputElement).value)}
                        onBlur={handle_amount_blur}
                      />
                      {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                    </div>
                    <p class="text-xs text-slate-500">{amount_hint}</p>
                  </div>
                )}

                <div class="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                  <EmailField label="To" value={preview.to} />
                  <EmailField label="Subject" value={preview.subject} />
                </div>

                <div class="space-y-1.5">
                  <Label htmlFor="short-pay-dispute-message">Message</Label>
                  <Textarea
                    id="short-pay-dispute-message"
                    rows={3}
                    value={message}
                    onInput={(e: Event) => setMessage((e.target as HTMLTextAreaElement).value)}
                  />
                </div>

                <div class="space-y-1.5">
                  <Label>Attachments</Label>
                  {preview.generated_attachments.map((name: string) => (
                    <div key={name} class="flex items-center gap-2 text-sm text-slate-600">
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span class="truncate">{name}</span>
                      <span class="text-xs text-slate-400">UNFI's pay inquiry form, filled in automatically</span>
                    </div>
                  ))}
                  {preview.attachments.map((a: { name: string; s3_uri: string }) => (
                    <div key={a.s3_uri} class="flex items-center gap-2 text-sm text-slate-600">
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span class="truncate">{a.name}</span>
                      <span class="text-xs text-slate-400">attached automatically</span>
                    </div>
                  ))}
                  {files.map((file: File, i: number) => (
                    <div key={`${file.name}-${i}`} class="flex items-center gap-2 text-sm text-slate-700">
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span class="truncate">{file.name}</span>
                      <button type="button" class="text-slate-400 hover:text-slate-700" onClick={() => remove_file(i)}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <input type="file" multiple class="block w-full text-sm text-slate-600" onChange={handle_files} />
                </div>

                <ul class="list-disc space-y-1 pl-5 text-sm text-slate-600">
                  {preview.instructions.map((step: string) => (
                    <li key={step}>{step}</li>
                  ))}
                </ul>
              </>
            )}

            {no_invoice_attached && !blocked && !preview.already_filed && (
              <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Attach the invoice before sending — without it the distributor's AP team has nothing showing what was owed, and
                the request will sit unanswered.
              </div>
            )}

            {error && <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          </div>
        )}

        {!loading && !preview && error && (
          <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <div class="flex justify-end gap-3 border-t border-slate-200 pt-4">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          {preview?.subject && (
            <Button
              onClick={() => void handle_send()}
              disabled={sending || loading || !preview.can_file || no_invoice_attached || amount_missing}>
              {sending ? "Sending…" : "Send request"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("effective_short_pay_amount", () => {
    it("prefers a positive typed amount, rounded to cents", () => {
      expect(effective_short_pay_amount({ amount: "1970.10" }, "12.345")).toBe(12.35)
    })
    it("falls back to the server amount when nothing usable was typed", () => {
      expect(effective_short_pay_amount({ amount: "1970.10" }, "")).toBe(1970.1)
      expect(effective_short_pay_amount({ amount: "1970.10" }, "0")).toBe(1970.1)
      expect(effective_short_pay_amount({ amount: "1970.10" }, "-4")).toBe(1970.1)
    })
    it("is zero when neither side knows", () => {
      expect(effective_short_pay_amount({ amount: "0" }, "")).toBe(0)
      expect(effective_short_pay_amount(null, "abc")).toBe(0)
    })
  })

  describe("short_pay_target_key", () => {
    it("distinguishes the two target kinds", () => {
      expect(short_pay_target_key(null)).toBe(null)
      expect(short_pay_target_key({ kind: "deduction", deduction: { id: "d1", invoice_number: "INV1" } })).toBe("deduction:d1")
      expect(short_pay_target_key({ kind: "short_pay", short_pay: { id: "s1" } as ShortPayJson })).toBe("short_pay:s1")
    })
  })
}
